// Lane Configuration Types - Per Plan

export type LaneType = 'FHIR_PAS' | 'PORTAL' | 'FAX_PHONE';

export interface PlanLaneConfig {
  plan_index: number;
  lanes_enabled: {
    FHIR_PAS: boolean;
    PORTAL: boolean;
    FAX_PHONE: boolean;
  };
  lane_priority: LaneType[]; // Order matters: first = highest priority
  portal_url: string | null;
  phone: string | null;
  fax: string | null;
}

// Default lane config for a new plan
export function createDefaultPlanLaneConfig(planIndex: number): PlanLaneConfig {
  return {
    plan_index: planIndex,
    lanes_enabled: {
      FHIR_PAS: false,    // Disabled by default (passive)
      PORTAL: true,       // Enabled by default
      FAX_PHONE: true,    // Enabled by default
    },
    lane_priority: ['PORTAL', 'FAX_PHONE'], // Default order (PAS added to front if enabled)
    portal_url: null,
    phone: null,
    fax: null,
  };
}

// Recalculate priority array based on enabled lanes
export function recalculateLanePriority(config: PlanLaneConfig): LaneType[] {
  const priority: LaneType[] = [];
  
  // FHIR_PAS always first if enabled
  if (config.lanes_enabled.FHIR_PAS) {
    priority.push('FHIR_PAS');
  }
  
  // Then PORTAL if enabled
  if (config.lanes_enabled.PORTAL) {
    priority.push('PORTAL');
  }
  
  // Then FAX_PHONE if enabled
  if (config.lanes_enabled.FAX_PHONE) {
    priority.push('FAX_PHONE');
  }
  
  return priority;
}

// Validate that at least one lane is enabled
export function validateLaneConfig(config: PlanLaneConfig): { valid: boolean; error: string | null } {
  const anyEnabled = config.lanes_enabled.FHIR_PAS || 
                     config.lanes_enabled.PORTAL || 
                     config.lanes_enabled.FAX_PHONE;
  
  if (!anyEnabled) {
    return { 
      valid: false, 
      error: 'At least one submission lane must be enabled. Enable PORTAL, FAX_PHONE, or FHIR_PAS.' 
    };
  }
  
  return { valid: true, error: null };
}

export const LANE_DISPLAY: Record<LaneType, { label: string; description: string }> = {
  FHIR_PAS: {
    label: 'FHIR PAS (Electronic)',
    description: 'Direct electronic submission via HL7 FHIR Prior Authorization API',
  },
  PORTAL: {
    label: 'Payer Portal',
    description: 'Web-based submission through payer provider portal',
  },
  FAX_PHONE: {
    label: 'Fax / Phone',
    description: 'Manual submission via fax or phone call',
  },
};
